/**
 * useTouchGestures Hook
 * 
 * Manages touch gestures including pinch-to-zoom and single-finger panning.
 * Uses native DOM event listeners with { passive: false } to allow preventDefault().
 */

import { useState, useRef, useCallback, useEffect } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface UseTouchGesturesOptions {
  minZoom: number;
  maxZoom: number;
  currentZoom: number;
  onZoomChange: (zoom: number) => void;
  onPanStart?: () => void;
  onPanMove?: (deltaX: number, deltaY: number) => void;
  onPanEnd?: () => void;
  elementRef: React.RefObject<HTMLCanvasElement | HTMLElement | null>; // Element to attach listeners to
}

export interface UseTouchGesturesReturn {
  isTouching: boolean;
}

interface TouchState {
  initialDistance: number | null;
  initialZoom: number;
  lastTouchX: number | null;
  lastTouchY: number | null;
  touchCount: number;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Calculate distance between two touch points
 */
function getTouchDistance(touch1: Touch, touch2: Touch): number {
  const dx = touch2.clientX - touch1.clientX;
  const dy = touch2.clientY - touch1.clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Get the midpoint between two touch points
 */
function getTouchMidpoint(touch1: Touch, touch2: Touch): { x: number; y: number } {
  return {
    x: (touch1.clientX + touch2.clientX) / 2,
    y: (touch1.clientY + touch2.clientY) / 2,
  };
}

// ============================================================================
// Hook
// ============================================================================

export function useTouchGestures(options: UseTouchGesturesOptions): UseTouchGesturesReturn {
  const {
    minZoom,
    maxZoom,
    currentZoom,
    onZoomChange,
    onPanStart,
    onPanMove,
    onPanEnd,
    elementRef,
  } = options;

  const [isTouching, setIsTouching] = useState(false);
  const touchStateRef = useRef<TouchState>({
    initialDistance: null,
    initialZoom: currentZoom,
    lastTouchX: null,
    lastTouchY: null,
    touchCount: 0,
  });

  // Store callbacks in refs to avoid recreating listeners
  const callbacksRef = useRef({
    onZoomChange,
    onPanStart,
    onPanMove,
    onPanEnd,
  });

  useEffect(() => {
    callbacksRef.current = {
      onZoomChange,
      onPanStart,
      onPanMove,
      onPanEnd,
    };
  }, [onZoomChange, onPanStart, onPanMove, onPanEnd]);

  // Update initialZoom when currentZoom changes
  useEffect(() => {
    touchStateRef.current.initialZoom = currentZoom;
  }, [currentZoom]);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const handleTouchStart = (evt: Event) => {
      const e = evt as TouchEvent;
      const touches = e.touches;
      touchStateRef.current.touchCount = touches.length;

      if (touches.length === 2) {
        // Two-finger pinch gesture
        e.preventDefault();
        const distance = getTouchDistance(touches[0], touches[1]);
        touchStateRef.current.initialDistance = distance;
        touchStateRef.current.initialZoom = currentZoom;
        setIsTouching(true);
      } else if (touches.length === 1) {
        // Single-finger pan gesture
        touchStateRef.current.lastTouchX = touches[0].clientX;
        touchStateRef.current.lastTouchY = touches[0].clientY;
        setIsTouching(true);
        if (callbacksRef.current.onPanStart) {
          callbacksRef.current.onPanStart();
        }
      }
    };

    const handleTouchMove = (evt: Event) => {
      const e = evt as TouchEvent;
      const touches = e.touches;

      if (touches.length === 2 && touchStateRef.current.initialDistance !== null) {
        // Two-finger pinch gesture - handle zoom
        e.preventDefault();
        
        const currentDistance = getTouchDistance(touches[0], touches[1]);
        const distanceRatio = currentDistance / touchStateRef.current.initialDistance;
        
        // Calculate new zoom based on the distance ratio
        let newZoom = touchStateRef.current.initialZoom * distanceRatio;
        
        // Clamp to valid range
        newZoom = Math.max(minZoom, Math.min(maxZoom, newZoom));
        
        // Apply zoom
        callbacksRef.current.onZoomChange(newZoom);
      } else if (touches.length === 1 && touchStateRef.current.lastTouchX !== null && touchStateRef.current.lastTouchY !== null) {
        // Single-finger pan gesture
        const touch = touches[0];
        const deltaX = touch.clientX - touchStateRef.current.lastTouchX;
        const deltaY = touch.clientY - touchStateRef.current.lastTouchY;
        
        touchStateRef.current.lastTouchX = touch.clientX;
        touchStateRef.current.lastTouchY = touch.clientY;
        
        if (callbacksRef.current.onPanMove) {
          callbacksRef.current.onPanMove(deltaX, deltaY);
        }
      }
    };

    const handleTouchEnd = (evt: Event) => {
      const e = evt as TouchEvent;
      const touches = e.touches;

      if (touches.length === 0) {
        // All fingers lifted
        touchStateRef.current.initialDistance = null;
        touchStateRef.current.lastTouchX = null;
        touchStateRef.current.lastTouchY = null;
        touchStateRef.current.touchCount = 0;
        setIsTouching(false);
        
        if (callbacksRef.current.onPanEnd) {
          callbacksRef.current.onPanEnd();
        }
      } else if (touches.length === 1 && touchStateRef.current.touchCount === 2) {
        // Went from two fingers to one - reset for single-finger pan
        touchStateRef.current.initialDistance = null;
        touchStateRef.current.lastTouchX = touches[0].clientX;
        touchStateRef.current.lastTouchY = touches[0].clientY;
        touchStateRef.current.touchCount = 1;
      } else if (touches.length === 2 && touchStateRef.current.touchCount === 1) {
        // Went from one finger to two - reset for pinch
        const distance = getTouchDistance(touches[0], touches[1]);
        touchStateRef.current.initialDistance = distance;
        touchStateRef.current.initialZoom = currentZoom;
        touchStateRef.current.lastTouchX = null;
        touchStateRef.current.lastTouchY = null;
        touchStateRef.current.touchCount = 2;
      }
    };

    // Register listeners with { passive: false } to allow preventDefault()
    element.addEventListener('touchstart', handleTouchStart, { passive: false });
    element.addEventListener('touchmove', handleTouchMove, { passive: false });
    element.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }, [elementRef, minZoom, maxZoom, currentZoom]);

  return {
    isTouching,
  };
}

